import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import {
  Users,
  FileText,
  FolderTree,
  ImageIcon,
  MessageSquare,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import Card from "../components/Card";
import useDashboard from "../hooks/useDashBoard";

const Dashboard = () => {
  const { getAllStats } = useDashboard();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [activityData, setActivityData] = useState([]);

  const mockActivityData = [
    { name: "Mon", users: 120, posts: 45 },
    { name: "Tue", users: 132, posts: 62 },
    { name: "Wed", users: 101, posts: 55 },
    { name: "Thu", users: 134, posts: 78 },
    { name: "Fri", users: 90, posts: 51 },
    { name: "Sat", users: 72, posts: 40 },
    { name: "Sun", users: 85, posts: 37 },
  ];

  useEffect(() => {
    setActivityData(mockActivityData);
    setLoading(false);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getAllStats();
        setStats(data);
        console.log("Fetched stats:", data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const StatCard = ({ title, value, change, icon: Icon }) => (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 rounded-md bg-blue-100 text-blue-600">
            <Icon className="h-6 w-6" />
          </div>
          <h3 className="ml-3 text-lg font-medium text-gray-900">{title}</h3>
        </div>
        <div
          className={`flex items-center ${
            change > 0
              ? "text-green-600"
              : change < 0
              ? "text-red-600"
              : "text-gray-500"
          }`}
        >
          {/* {change > 0 ? (
            <ArrowUp className="h-4 w-4 mr-1" />
          ) : change < 0 ? (
            <ArrowDown className="h-4 w-4 mr-1" />
          ) : null}
          <span className="text-sm font-medium">{Math.abs(change)}%</span> */}
        </div>
      </div>
      <div className="mt-4">
        <span className="text-3xl font-bold text-gray-900">{value}</span>
      </div>
    </Card>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your site's performance and activity
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-24 flex flex-col justify-between">
                <div className="flex justify-between">
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/6"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-1/2 mt-4"></div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard
            title="Users"
            value={stats?.users_total}
            // change={stats.users.change}
            icon={Users}
          />
          <StatCard
            title="Posts"
            value={stats?.posts_total}
            // change={stats.posts.change}
            icon={FileText}
          />
          <StatCard
            title="Categories"
            value={stats?.category_total}
            // change={stats.categories.change}
            icon={FolderTree}
          />
          <StatCard
            title="Payments"
            value={stats?.payments_total}
            // change={stats.payments.change}
            icon={ImageIcon}
          />{" "}
          <StatCard
            title="Post Details"
            value={stats?.post_detail_total}
            // change={stats.messages.change}
            icon={MessageSquare}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Weekly Activity">
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-pulse h-64 w-full bg-gray-200 rounded"></div>
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={activityData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="users" name="Users" fill="#3b82f6" />
                  <Bar dataKey="posts" name="Posts" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Recent Activity">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex items-start">
                  <div className="h-10 w-10 rounded-full bg-gray-200"></div>
                  <div className="ml-3 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <Users className="h-5 w-5" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    New user registered
                  </p>
                  <p className="text-xs text-gray-500">
                    John Doe - 2 hours ago
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    New post published
                  </p>
                  <p className="text-xs text-gray-500">
                    Jane Smith - 4 hours ago
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    New contact message
                  </p>
                  <p className="text-xs text-gray-500">
                    Mike Johnson - 6 hours ago
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                  <ImageIcon className="h-5 w-5" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    New image uploaded
                  </p>
                  <p className="text-xs text-gray-500">
                    Sarah Williams - 8 hours ago
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                  <FolderTree className="h-5 w-5" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    Category updated
                  </p>
                  <p className="text-xs text-gray-500">Admin - 10 hours ago</p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
